<template>
  <div>
    <AppModal
      v-if="marketSummary"
      :show="marketSummaryDialog"
      title="Market Summary"
      :modal-class="quoteItemTypes ? 'tw:max-w-[75%]' : 'tw:max-w-[900px]'"
      show-overflow
      data-testid="market-summary-dialog"
    >
      <template #content>
        <MarketSummary :coverage-type="marketSummary.coverageType" :layer-name="marketSummary.layerName" :layer-type="marketSummary.layerType" :quotes="marketSummary.quotes || undefined" :quote-item-types="quoteItemTypes"/>
        <div class="tw:grid tw:grid-cols-[auto_auto] tw:gap-4 tw:justify-end tw:p-4">
          <AppButton @click="downloadMarketSummary" data-testid="download-action">
            <Download :size="16" class="tw:mr-2" />
            Download Table
          </AppButton>
          <AppButton primary @click="closeMarketSummary" data-test="done-action">Done</AppButton>
        </div>
      </template>
    </AppModal>

    <div class="tw:flex tw:items-center tw:mt-4">
      <AppButton
        class="tw:btn-outline tw:btn-primary tw:rounded-full tw:ml-4 tw:mr-4"
        @click="$router.push(`/account/${$route.params.id}/quotes`)"
      >
        <ChevronLeft :size="16" class="tw:mr-1" />
        Quote Programs
      </AppButton>
      <h2 data-testid="quote-program-name-heading">{{ quoteProgram.programName }} ({{ quoteProgram.programTypeName }})</h2>
      <ConfirmModal title="Edit Program Name" confirm-text="Save"
        :is-confirm-disabled="() => quoteProgramName.trim() === ''"
        :action="saveQuoteProgram" :dismiss="cancelQuoteProgramEdit">

        <template v-slot:button="{ props: activatorProps }">
          <button
            type="button"
            title="Edit Program"
            v-bind="activatorProps"
            class="tw:btn tw:btn-ghost tw:btn-sm tw:btn-square tw:text-primary"
          >
            <Pencil :size="16" />
          </button>
        </template>
        <TextField class="tw:mt-2" v-model="quoteProgramName" :error="!quoteProgramName.trim()" :error-message="quoteProgramName.trim() ? undefined : 'Program name is required'" data-testid="quote-program-name"/>
      </ConfirmModal>
    </div>

    <div class="tw:flex tw:items-start tw:ml-4 tw:mt-4 tw:mb-4">
      <AppButton @click="addLayer()" data-testid="add-layer-btn">Add Layer</AppButton>
    </div>

    <div v-if="loading">
      <div class="skeleton quote-layer">
        <div class="q-header">
          <Skeleton shape="text" />
        </div>
        <GripVerticalIcon :size="16" class="quote-layer-drag-handle" />
        <div class="card" v-for="g in [1, 2, 3]" :key="g">
          <Skeleton shape="rectangle" width="100%" height="150px" />
        </div>
      </div>
    </div>
    <template v-else>
      <div v-if="!noData">

        <draggable class="quote-groups" data-testid="quote-group" v-model="quoteLayers" item-key="layerId" v-bind="{ ...dragOptions, group: 'layers' }" @end="dragLayerEnd()">
          <template #item="{ element, index: layerIndex }">
            <div class="quote-layer">
              <div class="q-header">
                <h2 class="q-item">{{ element.layerName }}</h2>
                <h4 class="q-sub-item"><p>Premium range</p>{{ premiumSummaryQuotes(element) }}</h4>
                <div style="position: absolute; top: 0; right: 0; margin: 0.4rem 0.1rem;" class="quote-header-actions">
                  <MenuDropdown :items="getLayerMenuItems(element)" left>
                    <template #action>
                      <label tabindex="0" class="tw:btn tw:btn-ghost tw:btn-sm tw:btn-square" :data-testid="`layer-actions-${layerIndex}`">
                        <EllipsisVertical :size="20" />
                      </label>
                    </template>
                  </MenuDropdown>
                </div>
              </div>
              <GripVertical :size="20" class="quote-layer-drag-handle" />
              <draggable 
                v-model="element.quotes" 
                item-key="quoteId"
                v-bind="{ ...dragOptions, group: 'quotes' }" 
                @start="dragStart()"
                @end="dragEnd()" 
                :empty-insert-threshold="100"
              >
                <template #item="{ element, index }">
                  <QuoteCard :quote="element" :handle-delete="handleDelete" :display-key="element.displayKey || index"
                    :class="{ 'quote-card-drag': drag }" :edit-action="editQuote" :copy-action="copyQuote(layerIndex)"/>
                </template>
              </draggable>
              <AppButton @click="addQuote(layerIndex)" class="tw:mt-2" :data-testid="`add-quote-${layerIndex}`">Add Quote</AppButton>
            </div>
          </template>
        </draggable>
        <!-- add / edit layer -->

        <AppModal
          :show="showLayerDialog"
          :title="(editingLayer && editingLayer.layerId ? 'Edit' : 'Add New') + ' Quote Layer'"
          cancel-button-label="Cancel"
          :action-button-label="(editingLayer && editingLayer.layerId ? 'Save' : 'Add') + ' Layer'"
          :handle-cancel="cancelLayerDialog"
          :handle-action="saveLayer"
          :action-disabled="isLayerConfirmDisabled()"
          size="lg"
          data-test="add-edit-layer-dialog"
        >
          <template #content>
            <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
              <TextField label="Name" data-testid="layer-name" v-model="quoteLayerName" />
              <div class="tw:form-control tw:w-full">
                <label class="tw:label"><span class="tw:label-text">Layer Type</span></label>
                <Combobox
                  data-testid="layer-type"
                  :model-value="selectedQuoteLayer"
                  :options="quoteLayerType.map(t => ({ label: t, value: t }))"
                  @update:model-value="setSelectedQuoteLayer"
                />
              </div>
            </div>
          </template>
        </AppModal>

      </div>

      <div class="no-data" v-else>
        No data available.
      </div>
    </template>

    <QuoteAddEditDialog :show-dialog="isQuoteAddEditDialogDisplayed"
      :title="addEditQuoteDialogTitle"
      :save-button-title="addEditQuoteDialogSaveTitle"
      :edit="editingQuote"
      :carriers="refData.carriers"
      :wholesaler-groups="refData.wholesalerGroups"
      :quote-item-types="quoteItemTypes || []"
      :quote-decline-reason-types="refData.quoteDeclineReasonTypes"
      :quote-response-types="refData.quoteResponseTypes"
      @cancel="addEditCancel" @confirm="saveQuote"
      :quote="selected.quote"
    />

    <!-- delete layer dialog -->

    <AppModal
      :show="deleteLayerDialog"
      title="Delete Layer"
      cancel-button-label="Cancel"
      action-button-label="Delete Layer"
      :handle-cancel="hideDeleteLayerDialog"
      :handle-action="deleteLayer"
      :action-disabled="deleting"
      :loading="deleting"
      size="lg"
    >
      <template #content>
        <p>Are you sure you want to delete this layer?</p>
      </template>
      <template #actions>
        <AppButton :disabled="deleting" @click="hideDeleteLayerDialog" data-test="cancel-layer-action">Cancel</AppButton>
        <AppButton primary :disabled="deleting" :loading="deleting" @click="deleteLayer" data-testid="confirm-layer-action">Delete Layer</AppButton>
      </template>
    </AppModal>
  </div>
</template>

<script lang='ts'>
import { carrierService, quoteService, referenceService, wholesalerGroupService } from '@/services/api';
import { calculateQuotePPM, Quote, QuoteItemType, QuoteLayer, QuoteProgram, QuoteRefDataType } from '@/types/Quote';
import draggable from 'vuedraggable';
import { Pencil, Download, EllipsisVertical, GripVertical, ChevronLeft, GripVerticalIcon } from 'lucide-vue-next';
import { Carrier, IssuingCompany } from '@/types/Carrier';
import QuoteCard from '@/components/QuoteCard.vue';
import MarketSummary from '@/views/reporting/MarketSummary.vue';
import { DownloadTableToCSV } from '@/utilities/Download';
import QuoteAddEditDialog from '@/components/QuoteAddEditDialog.vue';
import { WholesalerGroup } from '@/types/WholesalerGroup';
import { compactMoney } from '@/utilities/Money';
import ConfirmModal from '@/components/ConfirmModal.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppButton from '@/components/v2/AppButton.vue';
import MenuDropdown from '@/components/v2/MenuDropdown.vue';
import TextField from '@/components/v2/TextField.vue';
import Combobox from '@/components/v2/Combobox.vue';
import { useToast } from '@/composables/useToast';

const defaultSelectedState = {
  quote: null as null | Quote,
  carrierGroup: null as null | Carrier,
  issuingCompany: null as null | IssuingCompany,
  rawLayerIndex: -1, // represents the array index of layer
  layerIndex: -1,    // represents layer order, not necessarily sequential (e.g. 0, 1, 3, 10)
  layerId: -1,
  quoteItemValues: [] as { value?: string, dirty: boolean, valid?: boolean, error?: string }[]
};

export default {
  name: 'Quote',
  setup() {
    return { toast: useToast() };
  },
  components: {
    ConfirmModal,
    QuoteAddEditDialog,
    MarketSummary,
    QuoteCard,
    draggable,
    Skeleton,
    AppModal,
    AppButton,
    MenuDropdown,
    TextField,
    Combobox,
    Pencil,
    Download,
    EllipsisVertical,
    GripVertical,
    ChevronLeft,
    GripVerticalIcon,
  },
  data: () => ({
    loading: true,
    dateDialog: false,
    noData: false,
    quoteProgram: {} as QuoteProgram,
    quoteProgramName: '',
    quoteLayers: [] as QuoteLayer[],
    drag: false,
    dragOptions: { animation: 200, disabled: false, ghostClass: 'ghost' },
    refData: {
      carriers: [] as Carrier[],
      carriersMap: new Map<number, Carrier>(),
      wholesalerGroups: [] as WholesalerGroup[],
      quoteResponseTypes: [] as QuoteRefDataType[],
      quoteResponseTypesMap: new Map<number, QuoteRefDataType>(),
      quoteDeclineReasonTypes: [] as QuoteRefDataType[],
      quoteDeclineReasonTypesMap: new Map<number, QuoteRefDataType>()
    },
    selected: { ...defaultSelectedState },
    movedLayerIndex: -1,
    movedQuoteIndex: -1,
    issuingCompaniesLoading: false,
    issuingCompanies: [] as IssuingCompany[],
    deleting: false,
    quoteLayerName: '',
    quoteLayerType: ['Primary', 'Excess', 'Umbrella'],
    selectedQuoteLayer: '',
    deleteLayerDialog: false,
    deleteLayerId: null as null | number,
    quoteMatrix: [] as number[][],
    editingQuote: false,
    addingQuote: false,
    copyingQuote: false,
    editingLayer: null as null | QuoteLayer,
    marketSummary: undefined as { coverageType: string, quotes: Quote[] | null, layerName: string, layerType?: string } | undefined,
    quoteItemTypes: [] as QuoteItemType[],
    editingProgramName: false,
    savingProgram: false,
    rules: {
      required: (value: string) => (!!value && value.trim() !== '') || 'Program name is required'
    },
  }),
  computed: {
    marketSummaryDialog(): boolean {
      return !!this.marketSummary;
    },
    isQuoteAddEditDialogDisplayed() {
      return this.editingQuote || this.addingQuote || this.copyingQuote;
    },
    showLayerDialog() {
      return !!this.editingLayer;
    },
    addEditQuoteDialogTitle() {
      if (this.editingQuote) {
        return 'Edit Quote';
      }
      if (this.addingQuote) {
        return 'Add Quote';
      }
      if (this.copyingQuote) {
        return 'Copy Quote';
      }
      return 'Save Quote';
    },
    addEditQuoteDialogSaveTitle() {
      if (this.addingQuote) {
        return 'Add Quote';
      }
      return 'Save Quote';
    }
  },
  methods: {
    marketSummaryQuotes(layerId: number) {
      if (Array.isArray(this.quoteLayers) && this.quoteLayers.length > 0) {
        const found = this.quoteLayers.find(c => c.layerId === layerId);
        if (found && Array.isArray(found.quotes) && found.quotes.length > 0) {
          return found.quotes;
        }
      }
      return [];
    },
    premiumSummaryQuotes(layer: QuoteLayer): string {
      const layerId = layer.layerId || -1;
      if (Array.isArray(this.quoteLayers) && this.quoteLayers.length > 0) {
        const found = this.quoteLayers.find(c => c.layerId === layerId);
        if (found && Array.isArray(found.quotes) && found.quotes.length > 0) {
          const quotes = found.quotes.filter(x => x.responseStatus !== 'Declined' && !x.declined);
          quotes.sort((a, b) => (a.premium || 0) - (b.premium || 0));
          if (quotes.length > 1) {
            return `${compactMoney(quotes[0].premium || 0)} - ${compactMoney(quotes[quotes.length - 1].premium || 0)}`;
          } else if (quotes.length === 1) {
            return compactMoney(quotes[0].premium || 0);
          } else {
            return '';
          }
        }
      }
      return '';
    },
    canOpenMarketSummary(layerId: number) {
      return this.marketSummaryQuotes(layerId).length > 0;
    },
    getLayerMenuItems(element: QuoteLayer) {
      const items: { label: string; handler: () => void }[] = [];
      if (this.canOpenMarketSummary(element.layerId ?? 0)) {
        items.push({
          label: 'View Market Summary',
          handler: () => this.openMarketSummary(element.layerId ?? 0, element.layerName ?? '', element.layerType),
        });
      }
      items.push({ label: 'Edit Layer', handler: () => this.editLayer(element) });
      items.push({ label: 'Delete Layer', handler: () => this.showLayerDeleteDialog(element.layerId!) });
      return items;
    },
    openMarketSummary(layerId: number, layerName: string, layerType?: string) {
      const quotes = this.marketSummaryQuotes(layerId);
      const coverageType = this.quoteProgram.coverageType;
      this.marketSummary = { coverageType, quotes, layerName, layerType };
    },
    closeMarketSummary() {
      this.marketSummary = undefined;
    },
    downloadMarketSummary() {
      if (this.marketSummary) {
        const filename = `${this.marketSummary.coverageType.toLowerCase()}-${this.marketSummary.layerName.toLowerCase()}-layer-marketing-update.csv`;
        DownloadTableToCSV('#market-summary', filename);
      }
    },
    editQuote(quote: Quote) {
      this.selected = { ...defaultSelectedState };
      this.selected.quote = quote;

      this.editingQuote = true;
    },
    copyQuote(idx: number) {
      return (quote: Quote) => {
        this.selected = { ...defaultSelectedState };
        this.selected.quote = {
          ...quote, insurerGroup: undefined, insurerGroupId: undefined, issuingCompanyId: undefined,
          issuingCompanyName: undefined, wholesaler: undefined, wholesalerGroup: undefined, wholesalerGroupId: undefined,
          wholesalerId: undefined
        };
        this.selected.rawLayerIndex = idx;
        this.selected.layerIndex = this.quoteLayers[idx].layerIndex ?? -1;
        this.selected.layerId = this.quoteLayers[idx].layerId ?? -1;
        this.copyingQuote = true;
      };
    },
    isLayerConfirmDisabled() {
      return (this.selectedQuoteLayer === '' || this.quoteLayerName === '');
    },
    addQuote(idx: number) {
      this.selected.quote = {} as Quote;
      this.selected.rawLayerIndex = idx;
      this.selected.layerIndex = this.quoteLayers[idx].layerIndex ?? -1;
      this.selected.layerId = this.quoteLayers[idx].layerId ?? -1;

      this.addingQuote = true;
    },
    async dragLayerEnd() {
      this.quoteLayers.forEach((layer, idx) => {
        layer.layerIndex = idx;
      });
      await Promise.all([...this.quoteLayers.map(layer => quoteService.UpdateLayer(parseInt(this.$route.params.programId as string), layer))]);
    },
    dragStart() {
      this.drag = !this.drag;
    },
    async dragEnd() {
      this.drag = !this.drag;
      return this.sequenceQuotes();
    },
    async sequenceQuotes() {
      const items = this.quoteLayers.flatMap(layer =>
          layer.quotes.map((q, index) => ({
            layerId: Number(layer.layerId), quoteId: Number(q.quoteId), quoteIndex: index
          }))).filter(i => !isNaN(i.layerId) && !isNaN(i.quoteId));
      await quoteService.SequenceQuotes({ items });
    },
    carrierSelected(val: Carrier) {
      if (!val.ID) {
        return;
      }

      this.issuingCompanies = [];
      this.selected.issuingCompany = null;
      (async () => {
        this.issuingCompaniesLoading = true;
        this.issuingCompanies = await carrierService.issuingCompanies(val.ID);
        this.issuingCompaniesLoading = false;
      })();
    },
    dismissQuoteLayerDialog() {
      this.selectedQuoteLayer = '';
    },
    cancelLayerDialog() {
      this.editingLayer = null;
      this.dismissQuoteLayerDialog();
    },
    setSelectedQuoteLayer(val: string) {
      this.selectedQuoteLayer = val;
    },
    cancelQuoteProgramEdit() {
      this.quoteProgramName = this.quoteProgram.programName || '';
    },
    async saveQuoteProgram(): Promise<boolean> {
      if (this.quoteProgramName.trim() === this.quoteProgram.programName) {
        return false;
      }

      const qp = { ...this.quoteProgram, programName: this.quoteProgramName.trim() };
      this.savingProgram = true;
      try {
        await quoteService.UpdateQuoteProgram(this.$route.params.programId as string, qp);
        this.quoteProgram.programName = this.quoteProgramName.trim();
      } catch (e) {
        console.error(e);
        this.toast.error('Unable to save quote program.');
        this.quoteProgramName = this.quoteProgram.programName || '';
        return false;
      }

      return true;
    },
    async saveQuote(quote: Quote) {
      quote.displayKey = Math.random(); // forces a render (used as a value for displayKey prop on QuoteCard)

      if (this.editingQuote && quote.quoteId !== undefined) {
        try {
          await quoteService.UpdateQuote(quote.quoteId, quote);
          this.editingQuote = false;
        } catch (e) {
          console.error(e);
          this.toast.error('An error occurred while attempting to save quote.');
        }
      } else {
        quote.quoteId = -1;
        quote.programId = parseInt(this.$route.params.programId as string);
        quote.accountId = parseInt(this.$route.params.id as string);
        quote.coverageType = this.quoteProgram.coverageType;
        quote.layerIndex = this.selected.layerIndex;
        quote.layerId = this.selected.layerId;
        quote.quoteIndex = this.quoteLayers[this.selected.rawLayerIndex].quotes.length;
        quote.lowest = false;
        quote.declined = false;

        try {
          const quoteId = await quoteService.CreateQuote(quote);
          await this.sequenceQuotes();
          quote.quoteId = parseInt(quoteId);
          this.addingQuote = false;
          this.copyingQuote = false;
        } catch (e) {
          console.error(e);
          let title = 'An error occurred while attempting to create a new quote.';
          if (this.copyingQuote) {
            title = 'An error occurred while attempting to copy a quote.';
          }
          this.toast.error(title);
        }

        this.quoteLayers[this.selected.rawLayerIndex].quotes.push(quote);
      }

      this.selected = { ...defaultSelectedState };

      this.recomputeQuoteDisplayProperties();
    },
    addLayer() {
      this.editLayer({
        layerIndex: this.quoteLayers.reduce((maxIndex, l) => Math.max(l.layerIndex || 0, maxIndex), 0)
      } as QuoteLayer);
    },
    editLayer(layer: QuoteLayer) {
      this.editingLayer = layer;
      this.selectedQuoteLayer = layer.layerType || '';
      this.quoteLayerName = layer.layerName || '';
    },
    async saveLayer() {

      if (!this.editingLayer) {
        return;
      }

      const addNew = !this.editingLayer.layerId;

      try {

        this.editingLayer.layerName = this.quoteLayerName;
        this.editingLayer.layerType = this.selectedQuoteLayer;

        if (!addNew) {
          await quoteService.UpdateQuoteLayer(this.editingLayer, parseInt(this.$route.params.programId as string));
        } else {
          const layer = {
            ...this.editingLayer,
            layerIndex: this.quoteLayers.reduce((maxIndex, l) => Math.max(l.layerIndex || 0, maxIndex), 0) + 1,
            quotes: [],
            deleted: 0
          };

          let quoteLayerId = await quoteService.CreateQuoteLayer(layer, parseInt(this.$route.params.programId as string));
          layer.layerId = parseInt(quoteLayerId);
          this.quoteLayers.push({ ...layer });
          this.recomputeQuoteDisplayProperties();
        }

        this.selectedQuoteLayer = '';
        this.quoteLayerName = '';
        this.editingLayer = null;

      } catch (e) {
        console.error(e);
        this.toast.error(`An error occurred while attempting to ${addNew ? 'create a new' : 'update'} quote layer.`);
      }
    },
    showLayerDeleteDialog(layerIndex: number) {
      this.deleteLayerDialog = true;
      this.deleteLayerId = layerIndex;
    },
    hideDeleteLayerDialog() {
      this.deleteLayerDialog = false;
      this.deleteLayerId = null;
    },
    async handleDelete(quoteId: number | string) {
      this.deleting = true;

      await quoteService.DeleteQuote(quoteId);

      this.quoteLayers.every((layer) => {
        if (layer.quotes.findIndex(quote => quote.quoteId && String(quote.quoteId) === String(quoteId)) > -1) {
          layer.quotes = layer.quotes.filter(q => q.quoteId && String(q.quoteId) !== String(quoteId));
          return false;
        }
        return true;
      });
      this.recomputeQuoteDisplayProperties();

      this.deleting = false;
    },

    async deleteLayer() {
      if (this.deleteLayerId != null) {
        this.deleting = true;

        await quoteService.DeleteQuoteLayer(this.$route.params.programId as string, this.deleteLayerId);

        if (this.deleteLayerId != null) {
          this.quoteLayers = this.quoteLayers.filter(l => l.layerId !== this.deleteLayerId);
        }

        this.deleteLayerDialog = false;
        this.deleting = false;
      }
    },
    async refreshData(): Promise<void> {

      // quote program data

      this.quoteProgram = (await quoteService.GetQuotes(this.$route.params.programId as string)) || [];
      this.quoteProgramName = this.quoteProgram.programName || '';

      if (this.quoteProgram.quotes?.length === 0) {
        this.noData = true;
      }

      const quoteMap = (this.quoteProgram.quotes || []).reduce((layerMap, quote) => {
        if (!layerMap.has(quote.layerId)) {
          layerMap.set(quote.layerId, {
            layerIndex: quote.layerIndex,
            layerName: quote.layerName,
            layerType: quote.layerType,
            layerId: quote.layerId,
            quotes: []
          });
        }

        quote.displayKey = Math.random();
        quote.responseStatus = quote.responseStatusId ? this.refData.quoteResponseTypesMap.get(quote.responseStatusId)?.name : undefined;

        if (quote.quoteId !== null) {
          layerMap.get(quote.layerId).quotes.push(quote);
        }
        return layerMap;
      }, new Map());

      this.quoteLayers = Array.from(quoteMap.values());
      this.quoteLayers.sort((a, b) => (a.layerIndex ?? 0) - (b.layerIndex ?? 0));

      // load ref data

      const [carriers, wholesalerGroups, responseTypes, declineReasonTypes, quoteItemTypes] = await Promise.all([
        carrierService.list(),
        wholesalerGroupService.list(),
        referenceService.quoteResponseTypes(),
        referenceService.quoteDeclineReasonTypes(),
        referenceService.quoteItemTypesByCoverageType(this.quoteProgram.coverageTypeId)
      ]);

      if (quoteItemTypes) {
        quoteItemTypes.sort((a, b) => a.indexOrder - b.indexOrder);
      }

      this.quoteItemTypes = quoteItemTypes;
      this.refData.carriers = carriers;
      this.refData.carriersMap = carriers.reduce((acc, c) => {
        const carrierId = c.ID;
        if (isNaN(carrierId)) {
          return acc;
        }
        acc.set(carrierId, c);
        return acc;
      }, new Map<number, Carrier>());
      this.refData.wholesalerGroups = wholesalerGroups;

      this.refData.quoteResponseTypes = responseTypes;
      this.refData.quoteResponseTypesMap = responseTypes.reduce((acc, t) => {
        acc.set(t.id, t);
        return acc;
      }, new Map<number, QuoteRefDataType>());

      this.refData.quoteDeclineReasonTypes = declineReasonTypes;
      this.refData.quoteDeclineReasonTypesMap = declineReasonTypes.reduce((acc, t) => {
        acc.set(t.id, t);
        return acc;
      }, new Map<number, QuoteRefDataType>());
      this.recomputeQuoteDisplayProperties();
      this.loading = false;
    },
    recomputeQuoteDisplayProperties(): void {

      this.quoteLayers.forEach(layer => {
        layer.quotes.sort((a: Quote, b: Quote): number => a.quoteIndex - b.quoteIndex);

        const quotesWithPremiums = layer.quotes
            .filter((q: Quote) => q.premium && q.aggregateLimit) // filter out null and undefined quotes
            .sort((a: Quote, b: Quote): number => calculateQuotePPM(a)! - calculateQuotePPM(b)!);

        if (quotesWithPremiums.length > 0) {
          quotesWithPremiums.forEach((q, i) => {
            q.lowest = i === 0; // the 1st one is the lowest, set all others to false
            q.displayKey = Math.random();
          });
        }

        layer.quotes.forEach(q => {
          if (q.responseStatusId != null) {
            const status = this.refData.quoteResponseTypesMap.get(q.responseStatusId);
            if (status?.name === 'Declined') {
              q.declined = true;

              if (q.declinedReasonId) {
                const declineReason = this.refData.quoteDeclineReasonTypesMap.get(q.declinedReasonId);
                if (declineReason) {
                  q.declinedReason = declineReason.name;
                }
              }
            } else {
              q.declined = false;
            }
          }
        });
      });
    },
    addEditCancel(): void {
      this.selected = { ...defaultSelectedState };
      this.editingQuote = false;
      this.addingQuote = false;
      this.copyingQuote = false;
    }
  },
  watch: {
    quoteLayers() {
      this.recomputeQuoteDisplayProperties();
    }
  },
  created(): Promise<void> {
    return this.refreshData();
  }
};
</script>

<style scoped>

.quote-groups {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  overflow-x: auto;
  width: 100%;

  margin-bottom: 1rem;
}

.quote-layer {
  border: 1px dotted gray;
  border-radius: 4px;

  position: relative;

  display: grid;
  min-width: 15rem;
  margin: 0 0 0 1rem;
  padding-top: 0.75rem;

  text-align: center;

  cursor: grab;
  align-content: start;
}

.quote-layer:active {
  cursor: grabbing;
}

.q-sub-item > p {
  text-transform: uppercase;
  font-style: normal;
  font-size: 0.8em;
  font-weight: normal;
  color: #4b5563;
  margin-bottom: 0px;
}

.quote-layer-drag-handle {
  position: absolute;
  left: 2px;
  top: 2px;
}

.quote-card-drag {
  cursor: grabbing;
}

.ghost {
  opacity: 0.9;
  background: #e5e7eb;
  cursor: grabbing;
}

.flip-list-move {
  transition: transform 0.5s;
  cursor: grabbing;
}

.no-move {
  transition: transform 0s;
  cursor: grabbing;
}

.no-data {
  margin: 5rem auto 0 auto;
  text-align: center;
}

.q-header {
  display: inline-block;
}

.q-item {
  vertical-align: middle;
  text-wrap: normal;
  width: 75%;
  margin: 0 auto;
}

.dialog-content {
  background: white;
}

.dialog-buttons {
  display: grid;
  grid-template-columns: auto auto;
  grid-column-gap: 1rem;
  justify-content: end;
  padding: 1rem;
}

.skeleton {
  width: 15rem;
}

.skeleton .q-header {
  margin: 0 2rem;
}

.skeleton .card {
  padding: 1rem;
}
</style>
